import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { LoginReason, Role } from "./backendTypes";

const AUTH = "auth";
const TOKEN = "token";
const REDIRECT = "redirect";

export interface AuthState {
  loggedIn: boolean;
  reason: LoginReason | null;
  userId: number | null;
  roles: Role[];
  workingForLegalUnit: number | null;
}

export function defaultAuthState(): AuthState {
  return {
    loggedIn: false,
    reason: null,
    userId: null,
    roles: [],
    workingForLegalUnit: 0,
  };
}

const initialState = defaultAuthState();

export const authSlice = createSlice({
  name: AUTH,
  initialState,
  reducers: {
    setAuthState: (state, action: PayloadAction<AuthState>) => {
      state.loggedIn = action.payload.loggedIn;
      state.reason = action.payload.reason;
      state.userId = action.payload.userId;
      state.roles = action.payload.roles;
      state.workingForLegalUnit = action.payload.workingForLegalUnit;
    },
  },
});

export const { setAuthState } = authSlice.actions;

export default authSlice.reducer;

interface Session {
  status: Status;
}

interface Status {
  kind: StatusKind;
  reason: LoginReason | null;
  user_id: number | null;
  roles: Role[];
  working_for_legal_unit: number | null;
}

const enum StatusKind {
  Pending = "Pending",
  Allowed = "Allowed",
  Denied = "Denied",
}

export async function loadAuthState(): Promise<AuthState> {
  const token = localStorage.getItem(TOKEN);

  if (token === null) return Promise.resolve(defaultAuthState());

  const res = await axios.get<Session>(`/auth/login?token=${token}`);

  const session = res.data;

  let authState: AuthState;

  switch (session.status.kind) {
    case StatusKind.Pending: {
      authState = defaultAuthState();
      break;
    }

    case StatusKind.Denied: {
      authState = defaultAuthState();
      authState.reason = session.status.reason;
      break;
    }

    case StatusKind.Allowed: {
      authState = {
        loggedIn: true,
        reason: null,
        userId: session.status.user_id,
        roles: session.status.roles,
        workingForLegalUnit: session.status.working_for_legal_unit,
      };
      break;
    }
  }
  return authState;
}

interface NewLogin {
  uri: string;
  client_id: string;
  token: string;
}

export async function login() {
  const res = await axios.post<NewLogin>(`/auth/login`);
  const newLogin = res.data;

  setToken(newLogin.token);

  const endpoint = `${newLogin.uri}/connect/authorize`;

  const callback_redirect = `${window.location.origin}/auth/login_callback`;

  let client_redirect = getRedirect();
  if (!client_redirect || client_redirect === "/login") client_redirect = "/";

  const state = JSON.stringify({
    callback_redirect,
    client_redirect,
    token: newLogin.token,
  });

  const scope = "openid roles employee.vismaorganisationmaster email";

  const uri = `${endpoint}?client_id=${newLogin.client_id}&redirect_uri=${callback_redirect}&response_type=code&response_mode=form_post&scope=${scope}&state=${state}`;

  window.location.href = uri;
}

export async function logout() {
  const token = getToken();

  if (token == null) return;

  clearToken();
  await axios(`/auth/logout?token=${token}`, { method: "POST" });
}

function setToken(value: string) {
  localStorage.setItem(TOKEN, value);
}

export function getToken() {
  return localStorage.getItem(TOKEN);
}

export function clearToken() {
  localStorage.removeItem(TOKEN);
}

export function setRedirect(value: string) {
  localStorage.setItem(REDIRECT, value);
}

function getRedirect() {
  return localStorage.getItem(REDIRECT);
}
